/* eslint-disable camelcase */
import { LinkGithubAppInput, LinkGitlabAppInput } from '~/schemas/gitIntegrationsSchemas';
import {
  BranchConfig,
  DeployInformation,
  GithubInstallation,
  GithubRepo,
  GitlabInstallation,
  RepositoryBranch,
} from '~/types/resources';
import { integrationVersionControlApi } from '~/utils/api';

type SetGithubInstallationProps = {
  installationId: number;
  organizationId: string;
  token: string;
};

export const getGithubInstallationsForWorkspace = async (options: {
  organizationId: string;
  workspaceId: string;
}) =>
  integrationVersionControlApi.get<GithubInstallation[]>(
    `/organizations/${options.organizationId}/workspaces/${options.workspaceId}/installations/github`,
  );

export const getGitlabInstallationForWorkspace = async (options: {
  organizationId: string;
  workspaceId: string;
}) =>
  integrationVersionControlApi.get<GitlabInstallation | null>(
    `/organizations/${options.organizationId}/workspaces/${options.workspaceId}/installations/gitlab`,
  );

export const getGithubInstallationsForOrganization = async (options: { organizationId: string }) =>
  integrationVersionControlApi.get<GithubInstallation[]>(
    `/organizations/${options.organizationId}/installations/github`,
  );

export const getGitlabInstallationForOrganization = async (options: { organizationId: string }) =>
  integrationVersionControlApi.get<GitlabInstallation | null>(
    `/organizations/${options.organizationId}/installations/gitlab`,
  );

export const getGithubInstallationRepositories = async (options: {
  organizationId: string;
  workspaceId: string;
  installationId: number;
}) =>
  integrationVersionControlApi.get<GithubRepo[]>(
    `/organizations/${options.organizationId}/workspaces/${options.workspaceId}/github/installations/${options.installationId}/repositories`,
  );

export const getGitlabRepositories = async (options: {
  organizationId: string;
  workspaceId: string;
  installationId: string | number;
  search?: string;
}) =>
  integrationVersionControlApi.get<GithubRepo[]>(
    `/organizations/${options.organizationId}/workspaces/${options.workspaceId}/installations/${
      options.installationId
    }/gitlab/repositories${options.search ? `?search=${options.search}` : ''}`,
  );

export const getGithubRepositoryBranches = async (options: {
  organizationId: string;
  workspaceId: string;
  installationId: number;
  owner: string;
  repo: string;
}) =>
  integrationVersionControlApi.get<RepositoryBranch[]>(
    `/organizations/${options.organizationId}/workspaces/${options.workspaceId}/github/installations/${options.installationId}/branches/${options.owner}/${options.repo}`,
  );

export const getGitlabRepositoryBranches = async (options: {
  organizationId: string;
  workspaceId: string;
  installationId: string;
  repository: string;
}) =>
  integrationVersionControlApi.get<RepositoryBranch[]>(
    `/organizations/${options.organizationId}/workspaces/${options.workspaceId}/gitlab/installations/${options.installationId}/branches`,
    { repository: options.repository },
  );

interface GetGithubDeployInformationOptions {
  organizationId: string;
  workspaceId: string;
  installationId: number | string;
  robotId: string;
  repository: string;
}

export interface GetGithubDeployInformationResponse {
  currentDeploy: DeployInformation | null;
  nextDeploy: DeployInformation;
  config: BranchConfig;
}

export interface GitLabRegistrationParams {
  organizationId: string;
  registrationCode: string;
  installToken: string;
}

export interface GithubRegistrationParams {
  code: string;
  state: string;
  installationId?: string;
  organizationId: string;
}

export interface GitLabReconnectParams {
  organizationId: string;
  registrationCode: string;
  installationId: string;
}

export const getGithubDeployInformation = async ({
  organizationId,
  workspaceId,
  installationId,
  robotId,
  repository,
}: GetGithubDeployInformationOptions) =>
  integrationVersionControlApi.get<GetGithubDeployInformationResponse>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/installations/${installationId}/robots/${robotId}/deploy-information?repository=${repository}`,
  );

export const setGithubAppInstallation = async (props: SetGithubInstallationProps) =>
  integrationVersionControlApi.post<string>('/installation', props);

export const registerGithub = async (props: GithubRegistrationParams) =>
  integrationVersionControlApi.post<{
    status: 'redirect-to-org-installation' | 'installations-linked' | 'installations-available';
    installations: {
      id: string;
      account: { login: string; avatar_url: string };
      html_url: string;
      created_at: string;
    }[];
  }>(`/organizations/${props.organizationId}/oauth/github/register`, props);

export interface RegisterGithubLinkExistingInstallationParams {
  organizationId: string;
  installationIds: string[];
  token: string;
}

export const registerGithubLinkExistingInstallations = async (
  props: RegisterGithubLinkExistingInstallationParams,
) =>
  integrationVersionControlApi.post<{
    status: 'installations-linked' | 'installations-not-accessible';
    installations: GithubInstallation[];
  }>(
    `/organizations/${props.organizationId}/oauth/github/register/link-existing-installations`,
    props,
  );

export interface DeleteOrganizationParams {
  organizationId: string;
  installationId: string | number;
}

export const deleteOrganizationGithub = async (params: DeleteOrganizationParams) =>
  integrationVersionControlApi.delete(
    `/organizations/${params.organizationId}/github/installations/${params.installationId}`,
  );

export const deleteOrganizationGitlab = async (params: DeleteOrganizationParams) =>
  integrationVersionControlApi.delete(
    `/organizations/${params.organizationId}/gitlab/installations/${params.installationId}`,
  );

export const registerGitLab = async (props: GitLabRegistrationParams) =>
  integrationVersionControlApi.post<string>(
    `/organizations/${props.organizationId}/oauth/gitlab/register`,
    props,
  );

export const reconnectGitLab = async (props: GitLabReconnectParams) =>
  integrationVersionControlApi.post<string>(
    `/organizations/${props.organizationId}/gitlab/installation/${props.installationId}/reconnect`,
    props,
  );

export const setGithubAppRepo = async ({
  organizationId,
  workspaceId,
  installationId,
  ...rest
}: LinkGithubAppInput) =>
  integrationVersionControlApi.post<string>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/installations/${installationId}/repo-link/github`,
    rest,
  );
export const setGitlabAppRepo = async ({
  organizationId,
  workspaceId,
  installationId,
  ...rest
}: LinkGitlabAppInput) =>
  integrationVersionControlApi.post<string>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/installations/${installationId}/repo-link/gitlab`,
    rest,
  );

export interface GetGithubAppInstallationTokenParams {
  realm: string | undefined;
  organizationId: string | undefined;
  gitlabUrl?: string | undefined;
  gitlabClientId?: string | undefined;
  gitlabClientSecret?: string | undefined;
}
export const getGithbAppInstallationToken = async ({
  realm,
  organizationId,
  gitlabUrl,
  gitlabClientId,
  gitlabClientSecret,
}: GetGithubAppInstallationTokenParams) =>
  integrationVersionControlApi.post<{ token: string }>(
    `/organizations/${organizationId}/installation/init`,
    {
      instanceId: import.meta.env.VITE_INSTANCE_ID,
      realm,
      gitlabUrl,
      gitlabClientId,
      gitlabClientSecret,
    },
  );
interface PostDeployNextOptions {
  organizationId: string;
  workspaceId: string;
  installationId: number | string;
  robotId: string;
}

export const postGithubDeployNext = async ({
  organizationId,
  installationId,
  robotId,
  workspaceId,
}: PostDeployNextOptions) =>
  integrationVersionControlApi.post<DeployInformation | undefined>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/installations/${installationId}/robots/${robotId}/deploy-next`,
    {},
  );

export const postGitlabDeployNext = async ({
  organizationId,
  installationId,
  robotId,
  workspaceId,
}: PostDeployNextOptions) =>
  integrationVersionControlApi.post<DeployInformation | undefined>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/installations/${installationId}/robots/${robotId}/deploy-next/gitlab`,
    {},
  );

interface UnlinkRobotFromRepoOptions {
  organizationId: string;
  workspaceId: string;
  installationId: number | string;
  robotId: string;
  repository: string;
}

export const unlinkRobotFromRepo = async ({
  organizationId,
  workspaceId,
  installationId,
  robotId,
  repository,
}: UnlinkRobotFromRepoOptions) =>
  integrationVersionControlApi.post(
    `/organizations/${organizationId}/workspaces/${workspaceId}/installations/${installationId}/robots/${robotId}/repo-unlink`,
    {
      repository,
    },
  );
